<template>
    <div id="app">
        <router-view />
        <profile-verification-modal />
        <small-screen-warning />
        <app-update-prompt v-if="versionState.promptOpen" :latest="versionState.latest" @reload="reloadApp" @later="snoozePrompt" />
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { axiosInstance } from "./http_client";
import { installVersionCheck, versionState, reloadApp, snoozePrompt } from "./utils/appVersion";
import AppUpdatePrompt from "./components/AppUpdatePrompt";
import ProfileVerificationModal from "./components/ProfileVerificationModal";
import SmallScreenWarning from "./components/SmallScreenWarning";

export default {
    components: { ProfileVerificationModal, SmallScreenWarning, AppUpdatePrompt },
    data() {
        return { versionState };
    },
    computed: {
        ...mapGetters(['currentUser'])
    },
    methods: {
        ...mapActions(['getEnrollments', 'getRegistrations', 'getBillingState', 'getCertifications']),
        reloadApp,
        snoozePrompt
    },
    mounted() {
        console.log('App mounted');

        // Stale-build prompt: compares the API's Latest-App-Version header
        // with this bundle's version and suggests a reload (utils/appVersion).
        installVersionCheck({ axiosInstance, router: this.$router });

        // if (!localStorage.getItem('token')) this.$router.push('/');

        // Skip the boot loads when landing on a provider login relay: the
        // relay is about to replace the user, and loads fired here would
        // race the dashboard's own loads for the new user. Read the hash
        // directly — the lazy relay route hasn't resolved yet at mount, so
        // $route still reports '/'.
        const relayRoute = /^#\/providers\//.test(window.location.hash);
        if (this.currentUser && !relayRoute) {
            this.getEnrollments();
            this.getRegistrations();
            // payment plans / cart / expired summary — dashboard banners + Account page
            this.getBillingState();
            // per-certification switches — gate the PD surfaces + application rows
            this.getCertifications();
        }
    }
};
</script>

<style lang="scss">
// Animate
$animationDuration: 0.5s; // specify animation duration. Default value: 1s
@import "~vue2-animate/src/sass/vue2-animate.scss";

// Shared thin progress bar (enrollment/registration cards, dashboard tiles)
@import "./assets/scss/progress";

// BUEFY
// Import Bulma's core
@import "~bulma/sass/utilities/_all";

$body-size: 16px;

// Tweaks to animation and fix to slide animation on collapse
$speed-slow: 600ms !default;
$speed-slower: 800ms !default;

.slide-enter-to,
.slide-leave {
    max-height: 999px !important;
    overflow: hidden;
}

// Set your colors
$primary: #336699;
$primary-light: findLightColor($primary);
$primary-dark: findDarkColor($primary);
$primary-invert: findColorInvert($primary);
$twitter: #4099FF;
$twitter-invert: findColorInvert($twitter);

$danger: #9b0d0d;
$danger-light: findLightColor($danger);
$danger-dark: findDarkColor($danger);

$warning: #e28b05;
$warning-light: findLightColor($warning);
$warning-dark: findDarkColor($warning);

// Lists and maps
$custom-colors: null !default;
$custom-shades: null !default;

// Setup $colors to use as bulma classes (e.g. 'is-twitter')
$colors: mergeColorMaps(("white": ($white,
                $black,
            ),
            "black": ($black,
                $white,
            ),
            "light": ($light,
                $light-invert,
            ),
            "dark": ($dark,
                $dark-invert,
            ),
            "primary": ($primary,
                $primary-invert,
                $primary-light,
                $primary-dark,
            ),
            "link": ($link,
                $link-invert,
                $link-light,
                $link-dark,
            ),
            "info": ($info,
                $info-invert,
                $info-light,
                $info-dark,
            ),
            "success": ($success,
                $success-invert,
                $success-light,
                $success-dark,
            ),
            "warning": ($warning,
                $warning-invert,
                $warning-light,
                $warning-dark,
            ),
            "danger": ($danger,
                $danger-invert,
                $danger-light,
                $danger-dark,
            ),
        ),
        $custom-colors );

// Links
$link: $primary;
$link-invert: $primary-invert;
$link-focus-border: $primary;

// Messages
$message-body-padding: .8em 1.7em;

// Modals / Dropdowns
$modal-z: 500;
$dropdown-content-z: 500;

// Import Bulma and Buefy styles
@import "~bulma";
@import "~buefy/src/scss/buefy";

// Shared CTA strip + global small-button radius. Must come AFTER Bulma:
// it overrides .button.is-small's 2px radius at equal specificity.
@import "./assets/scss/cta";
@import "./assets/scss/layout";

// Course content frame insets (the white lesson card and its controls are
// absolutely positioned inside the main panel). Tightens with the viewport
// so lessons stay readable on tablets and phones.
:root {
    --course-frame-top: 1.5rem;
    --course-frame-left: 5rem;
    --course-frame-right: 4rem;
    --course-frame-bottom: 7rem;
    --course-frame-pad: 2rem;
    --course-controls-bottom: 1.5rem;
    --course-controls-pad-top: 3rem;

    @media (max-width: 1180px) {
        --course-frame-top: 1rem;
        --course-frame-left: 2rem;
        --course-frame-right: 2rem;
    }

    @media (max-width: 768px) {
        --course-frame-top: 0.75rem;
        --course-frame-left: 0.5rem;
        --course-frame-right: 0.5rem;
        // the controls stack into two rows here
        --course-frame-bottom: 8.25rem;
        --course-frame-pad: 1rem;
        --course-controls-bottom: 0.5rem;
        --course-controls-pad-top: 0.5rem;
    }
}

html,
body {
    overflow: hidden;
    padding: 0 !important;
    margin: 0 !important;
    background: white;
}

body {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    overflow-x: hidden;
    overflow-y: auto;
}

#app {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    height: 100%;
    margin: 0;
}

.notification .notification-message {
    .title {
        font-size: 1.3rem;
        margin-bottom: 1rem;
    }

    .message {
        background-color: white !important;
    }
}

.timeline {
    list-style: none !important;
}

.dropdown .background {
    z-index: 499;
}

.dropdown.is-mobile-modal>.dropdown-menu {
    z-index: 500 !important;
}
</style>
